// Grouped client for Docuseal operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { Submission } from '../models/Submission';
import { FilterSubmissionRequest } from '../models/FilterSubmissionRequest';
import { DataviewSubmissionResponse } from '../models/DataviewSubmissionResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { SubmissionSerializer } from '../serializers/SubmissionSerializer';
import { FilterSubmissionRequestSerializer } from '../serializers/FilterSubmissionRequestSerializer';
import { DataviewSubmissionResponseSerializer } from '../serializers/DataviewSubmissionResponseSerializer';

/**
 * HTTP client for Docuseal operations
 *
 * Client for Docuseal operations
 */
export class DocusealClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForSubmission(formName: string, objectId: string, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, lastModifiedOn?: string | null, flowId?: string | null, isActive?: boolean | null, objectStatusId?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/docuseal.Submission({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (lastModifiedOn !== undefined) {
      queryParams.append('LastModifiedOn', String(lastModifiedOn));
    }
    if (flowId !== undefined) {
      queryParams.append('FlowId', String(flowId));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    if (objectStatusId !== undefined) {
      queryParams.append('ObjectStatusId', String(objectStatusId));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeSubmission(formName: string, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, lastModifiedOn?: string | null, flowId?: string | null, isActive?: boolean | null, objectStatusId?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/docuseal.Submission/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (lastModifiedOn !== undefined) {
      queryParams.append('LastModifiedOn', String(lastModifiedOn));
    }
    if (flowId !== undefined) {
      queryParams.append('FlowId', String(flowId));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    if (objectStatusId !== undefined) {
      queryParams.append('ObjectStatusId', String(objectStatusId));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterSubmission(request?: FilterSubmissionRequest | null, options?: { headers?: Record<string, string> }): Promise<Submission[]> {
    const path = '/app/api/Object/docuseal.Submission/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterSubmissionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => SubmissionSerializer.fromObject(item));
  }

  async recentSubmission(request?: FilterSubmissionRequest | null, options?: { headers?: Record<string, string> }): Promise<Submission[]> {
    const path = '/app/api/Object/docuseal.Submission/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterSubmissionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => SubmissionSerializer.fromObject(item));
  }

  async dataViewSubmission(request?: FilterSubmissionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewSubmissionResponse> {
    const path = '/app/api/Object/docuseal.Submission/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterSubmissionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewSubmissionResponseSerializer.fromObject(data);
  }

}
